// openedAt is when the circuit first opened - never rewritten while it stays open, so it tells an
// operator how long the thing has been failing. windowStartedAt is the start of the current wait
// window, which every probe restarts. Both are set together whenever the state leaves CLOSED, so the
// union lets a reader that has ruled out CLOSED treat them as numbers.
export type CircuitEntry =
  | { state: 'CLOSED'; failureCount: number; openedAt: null; windowStartedAt: null }
  | { state: 'OPEN' | 'HALF_OPEN'; failureCount: number; openedAt: number; windowStartedAt: number };

const CLOSED_ENTRY: CircuitEntry = { state: 'CLOSED', failureCount: 0, openedAt: null, windowStartedAt: null };

type CreateCircuitBreakerProps = {
  // Key the state is stored under on globalThis - unique per breaker.
  name: string;
  threshold: number;
  resetTimeoutMs: number;
};

/**
 * In-process circuit breaker, keyed so one breaker can guard many independent things
 * (pass no key for a single circuit). Opens after `threshold` consecutive failures and
 * lets one probe through every `resetTimeoutMs` to detect recovery.
 *
 * A granted probe restarts the window, so a probe that never reports back - the request was
 * served from cache, or the guarded call turned out to be skipped for an unrelated reason -
 * costs one window rather than wedging the circuit shut forever. A caller that knows it did
 * not use the probe can hand it straight back with `releaseProbe`.
 *
 * State lives on `globalThis` rather than in module scope, matching the memory cache idiom,
 * so Next's per-route server bundles cannot end up holding two separate circuits.
 */
export const createCircuitBreaker = ({ name, threshold, resetTimeoutMs }: CreateCircuitBreakerProps) => {
  const globals = globalThis as Record<string, unknown>;
  // Null prototype on both paths: keys come from CMS config, and a name like `constructor` would
  // otherwise resolve to an inherited member and wedge that circuit permanently.
  // Frozen: it is handed out by every read on a cold breaker, so a write through a read result
  // would poison every circuit for the process lifetime and reset() could not clear it.
  const NO_ENTRIES: Record<string, CircuitEntry> = Object.freeze(Object.create(null));
  const readEntries = (): Record<string, CircuitEntry> => (globals[name] as Record<string, CircuitEntry>) ?? NO_ENTRIES;
  const writeEntries = (): Record<string, CircuitEntry> => {
    globals[name] ??= Object.create(null);
    return globals[name] as Record<string, CircuitEntry>;
  };

  const windowElapsed = (entry: CircuitEntry): boolean =>
    entry.windowStartedAt !== null && Date.now() - entry.windowStartedAt >= resetTimeoutMs;

  const isBlocked = (key = ''): boolean => {
    const entry = readEntries()[key];
    return !!entry && entry.state !== 'CLOSED' && !windowElapsed(entry);
  };

  const tryProbe = (key = ''): boolean => {
    // Read before writing, so a healthy circuit never creates its global entry.
    const entry = readEntries()[key];
    if (!entry || entry.state === 'CLOSED' || !windowElapsed(entry)) return false;

    writeEntries()[key] = { ...entry, state: 'HALF_OPEN', windowStartedAt: Date.now() };
    return true;
  };

  const recordSuccess = (key = ''): void => {
    if (readEntries()[key]) delete writeEntries()[key];
  };

  return {
    isBlocked,
    tryProbe,
    recordSuccess,

    shouldSkipCall: (key = ''): boolean => !tryProbe(key) && isBlocked(key),

    recordFailure: (key = ''): void => {
      const existing = readEntries()[key];
      const entry = existing || CLOSED_ENTRY;
      const now = Date.now();

      // Already open: the window is ticking. Counting in-flight stragglers here would push the
      // reopen time past the one already announced and inflate failureCount past the threshold.
      if (entry.state === 'OPEN') return;

      // The probe failed, so wait another full window. openedAt stays put - it dates the outage -
      // while failureCount keeps counting, so it reflects the real number of failed calls rather
      // than freezing at the threshold for the length of an outage.
      if (entry.state === 'HALF_OPEN') {
        writeEntries()[key] = {
          ...entry,
          state: 'OPEN',
          failureCount: entry.failureCount + 1,
          windowStartedAt: now,
        };
        return;
      }

      const failureCount = entry.failureCount + 1;
      writeEntries()[key] =
        failureCount >= threshold
          ? { state: 'OPEN', failureCount, openedAt: now, windowStartedAt: now }
          : { ...entry, failureCount };
    },

    // Hands an unused probe back so the next request can take it, instead of spending the window on
    // a request that never made the call. Skipping this only delays recovery by one window.
    releaseProbe: (key = ''): void => {
      const entry = readEntries()[key];
      if (entry?.state !== 'HALF_OPEN') return;

      const entries = writeEntries();
      // Backdate the window so the next caller can probe immediately, and re-insert the key so it
      // goes to the back of keys(): a caller that keeps handing the probe back must not keep taking
      // it, or every other circuit behind it would starve and never be probed at all.
      delete entries[key];
      entries[key] = { ...entry, state: 'OPEN', windowStartedAt: Date.now() - resetTimeoutMs };
    },

    getState: (key = ''): CircuitEntry => ({ ...(readEntries()[key] || CLOSED_ENTRY) }),

    keys: (): string[] => Object.keys(readEntries()),

    reset: (): void => {
      delete globals[name];
    },
  };
};
